import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { api, formatApiErrorDetail, inr } from "../lib/api";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";
import EmotionSelector from "./journal/EmotionSelector";
import ScreenshotGrid from "./journal/ScreenshotGrid";
import AIInsightPanel from "./journal/AIInsightPanel";

const LABEL_CLS = "text-[10px] tracking-[0.25em] uppercase text-[#71717A]";

function qtyLabel(trade) {
  return trade.lots ? `${trade.quantity} (${trade.lots} × ${trade.lot_size})` : trade.quantity;
}

export default function JournalDialog({ open, onClose, trade, onSaved }) {
  const [emotion, setEmotion] = useState("");
  const [notes, setNotes] = useState("");
  const [screenshots, setScreenshots] = useState([]);
  const [aiInsight, setAiInsight] = useState("");
  const [busy, setBusy] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open || !trade) return;
    setEmotion(trade.emotion || "");
    setNotes(trade.notes || "");
    setScreenshots(trade.screenshots || []);
    setAiInsight(trade.ai_insight || "");
  }, [open, trade]);

  const journalPayload = () => ({ emotion: emotion || null, notes, screenshots });

  async function handleFiles(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const added = [];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ path: data.path, name: data.name, size: data.size, uploaded_at: new Date().toISOString() });
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
      }
    }
    setScreenshots((prev) => [...prev, ...added]);
    setUploading(false);
    e.target.value = "";
  }

  async function save() {
    setBusy(true);
    try {
      await api.put(`/trades/${trade.id}`, journalPayload());
      toast.success("Journal saved");
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function analyze() {
    setAnalyzing(true);
    try {
      await api.put(`/trades/${trade.id}`, journalPayload()); // persist current state first
      const { data } = await api.post(`/trades/${trade.id}/ai-analyze`);
      setAiInsight(data.insight);
      onSaved && onSaved();
      toast.success("AI insight generated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Analyze failed");
    } finally {
      setAnalyzing(false);
    }
  }

  if (!trade) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121318] border-[#27272A] text-white max-w-3xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="journal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="text-xs tracking-[0.25em] uppercase text-[#A1A1AA]">Trade Journal</span>
            <span className="text-base text-white font-mono">{trade.stock} · {trade.transaction}</span>
          </DialogTitle>
          <DialogDescription className="sr-only">Record emotion, notes and chart screenshots for this trade.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#27272A] border border-[#27272A] text-xs">
          <Stat label="Entry" value={trade.rate?.toFixed(2)} />
          <Stat label="Qty" value={qtyLabel(trade)} />
          <Stat label="Invested" value={inr(trade.total_invested)} />
          <Stat label="Status" value={trade.status} />
        </div>

        <div>
          <Label className={LABEL_CLS}>Emotion at entry</Label>
          <div className="mt-2">
            <EmotionSelector value={emotion} onChange={setEmotion} testidPrefix="emotion" rowTestid="emotion-row" colored />
          </div>
        </div>

        <div>
          <Label className={LABEL_CLS}>Notes — setup, context, lessons</Label>
          <Textarea data-testid="journal-notes" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Why did you take this trade? What was the setup? What did you learn?"
            className="mt-2 bg-[#08080A] border-[#27272A] text-white rounded-sm placeholder:text-[#71717A]" />
        </div>

        <div>
          <div className="flex items-center justify-between">
            <Label className={LABEL_CLS}>Chart Screenshots</Label>
            <Button type="button" data-testid="journal-upload-btn" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="bg-transparent border-[#27272A] text-[#A1A1AA] hover:bg-[#1A1B23] hover:text-white rounded-sm h-7">
              {uploading ? <Loader2 className="h-3 w-3 animate-spin mr-2" /> : <Upload className="h-3 w-3 mr-2" />}
              <span className="text-[10px] tracking-wider">Add Image</span>
            </Button>
          </div>
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" data-testid="journal-file-input" />
          <ScreenshotGrid screenshots={screenshots} onRemove={(path) => setScreenshots((prev) => prev.filter((s) => s.path !== path))} />
        </div>

        <AIInsightPanel insight={aiInsight} analyzing={analyzing} onAnalyze={analyze} />

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="journal-close"
            className="bg-transparent border-[#27272A] text-[#A1A1AA] hover:bg-[#1A1B23] hover:text-white rounded-sm">Close</Button>
          <Button onClick={save} disabled={busy} data-testid="journal-save" className="bg-[#007AFF] hover:bg-[#007AFF]/90 text-white rounded-sm">
            {busy ? "Saving…" : "Save Journal"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[#121318] px-3 py-2">
      <div className="text-[9px] tracking-[0.2em] uppercase text-[#71717A]">{label}</div>
      <div className="text-sm font-mono text-white mt-0.5 truncate">{value ?? "—"}</div>
    </div>
  );
}
